import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MoveTrainer } from '@/components/MoveTrainer';
import { MistakeLibrary } from '@/components/MistakeLibrary';
import { ArrowLeft, BookOpen, Target, BarChart3 } from 'lucide-react';
import { useSEO } from '@/hooks/useSEO';
import { useNavigate } from 'react-router-dom';
import type { TrainingPosition } from '@/hooks/useTrainingPositions';

export default function Training() {
  const navigate = useNavigate();
  
  useSEO({
    title: 'Move Trainer - Practice Your Mistakes | ChessHatch',
    description: 'Improve your chess game by practicing positions where you made mistakes. Personalized training based on your game analysis.',
    keywords: 'chess trainer, move trainer, chess mistakes, chess practice, improve chess'
  });

  const [trainingPositions, setTrainingPositions] = useState<TrainingPosition[]>([]);
  const [isTraining, setIsTraining] = useState(false);

  const handleStartTraining = (positions: TrainingPosition[]) => {
    setTrainingPositions(positions);
    setIsTraining(true);
  };

  const handleCompleteTraining = () => {
    setIsTraining(false);
    setTrainingPositions([]);
  };

  const handleBackToLibrary = () => {
    setIsTraining(false);
    setTrainingPositions([]);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-950/95 to-slate-950 px-4 py-8">
      <div className="container mx-auto max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-start justify-between mb-4">
          {isTraining ? (
            <Button
              variant="ghost"
              onClick={handleBackToLibrary}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Library
            </Button>
          ) : (
            <div />
          )}
          
          {!isTraining && (
            <Button 
              variant="outline" 
              onClick={() => navigate('/training/stats')}
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              View Statistics
            </Button>
          )}
        </div>

        <div className="flex items-center gap-3 mb-2">
          <Target className="w-8 h-8 text-primary" />
          <h1 className="text-4xl font-bold">Move Trainer</h1>
        </div>
        <p className="text-muted-foreground">
          {isTraining 
            ? 'Practice the positions where you made mistakes'
            : 'Master your weaknesses by training on your past mistakes'
          }
        </p>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {isTraining ? (
          <>
            {/* Training Board - Takes 2/3 width */}
            <div className="lg:col-span-2">
              <MoveTrainer
                positions={trainingPositions}
                onComplete={handleCompleteTraining}
              />
            </div>

            {/* Info Sidebar */}
            <div className="space-y-4">
              <Card className="p-6">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  Training Tips
                </h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Take your time to analyze each position</li>
                  <li>• Look for tactical patterns</li>
                  <li>• Consider what makes a move better</li>
                  <li>• Review the solution even if you got it right</li>
                  <li>• Come back to review positions regularly</li>
                </ul>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold mb-3">Spaced Repetition</h3>
                <p className="text-sm text-muted-foreground">
                  Positions are scheduled for review based on your performance. 
                  Master a position by solving it correctly multiple times.
                </p>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold mb-3">Mastery Levels</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-gray-400" />
                    <span>Level 0: Not attempted</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <span>Level 1-2: Learning</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500" />
                    <span>Level 3-4: Improving</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span>Level 5: Mastered</span>
                  </div>
                </div>
              </Card>
            </div>
          </>
        ) : (
          <div className="lg:col-span-3">
            <MistakeLibrary onStartTraining={handleStartTraining} />
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
